import type { FilterPillOption } from "../FilterPillGrid";
import type { RelativePeriodApi, WeekdayApi } from "../../../services/storyCanvas/storyFilters.types";

/** An absolute range, ISO yyyy-mm-dd both ends inclusive. */
export interface DatePresetRange {
  from: string;
  to: string;
}

const iso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const day = (year: number, month: number, date: number): Date => new Date(year, month, date);

/** The spec's period units, as `RelativePeriodApi` names them. */
export type PeriodUnit = "Day" | "Week" | "Month" | "Quarter" | "Year";

/** The platform's first day of the week, when the server names none. */
export const DEFAULT_WEEK_START: WeekdayApi = "monday";

/** Each weekday as `Date.getDay()` numbers it. */
const DAY_INDEX: Record<WeekdayApi, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * The first day of the unit holding `date`. A week starts on `weekStart`: the story's effective
 * one from the filters list (model calendar, else tenant setting, else Monday), as the query engine counts.
 */
const unitStart = (unit: PeriodUnit, date: Date, weekStart: WeekdayApi): Date => {
  const y = date.getFullYear();
  const m = date.getMonth();
  if (unit === "Week") return day(y, m, date.getDate() - ((date.getDay() - DAY_INDEX[weekStart] + 7) % 7));
  if (unit === "Month") return day(y, m, 1);
  if (unit === "Quarter") return day(y, Math.floor(m / 3) * 3, 1);
  if (unit === "Year") return day(y, 0, 1);
  return day(y, m, date.getDate());
};

/** `date` moved by `n` units (negative: back). */
const addUnits = (unit: PeriodUnit, date: Date, n: number): Date => {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  if (unit === "Week") return day(y, m, d + 7 * n);
  if (unit === "Month") return day(y, m + n, d);
  if (unit === "Quarter") return day(y, m + 3 * n, d);
  if (unit === "Year") return day(y + n, m, d);
  return day(y, m, d + n);
};

const isUnit = (unit: string | undefined): unit is PeriodUnit =>
  unit === "Day" || unit === "Week" || unit === "Month" || unit === "Quarter" || unit === "Year";

/**
 * A rolling period's dates today, as the spec reads it: `last` the `count` whole units before today's
 * (today left out: Last 30 days ends yesterday), `this` the whole unit holding today, `to_date` the
 * unit's start through today. Null for a period this can't read.
 */
export const toPeriodRange = (
  period: RelativePeriodApi,
  now: Date,
  weekStart: WeekdayApi = DEFAULT_WEEK_START,
): DatePresetRange | null => {
  const today = day(now.getFullYear(), now.getMonth(), now.getDate());
  if (isUnit(period.this)) {
    const start = unitStart(period.this, today, weekStart);
    return { from: iso(start), to: iso(addUnits("Day", addUnits(period.this, start, 1), -1)) };
  }
  if (isUnit(period.to_date)) return { from: iso(unitStart(period.to_date, today, weekStart)), to: iso(today) };
  if (isUnit(period.last)) {
    const count = period.count ?? 1;
    if (!Number.isInteger(count) || count < 1) return null;
    const current = unitStart(period.last, today, weekStart);
    return { from: iso(addUnits(period.last, current, -count)), to: iso(addUnits("Day", current, -1)) };
  }
  return null;
};

/** A preset: the rolling period it saves as (the spec's period words, as data-app period controls send them). */
interface DatePreset {
  id: string;
  label: string;
  period: RelativePeriodApi;
}

/** The design's first row. */
const LIVE_PRESETS: DatePreset[] = [
  { id: "today", label: "Today", period: { this: "Day" } },
  { id: "last7", label: "Last 7 days", period: { last: "Day", count: 7 } },
  { id: "last30", label: "Last 30 days", period: { last: "Day", count: 30 } },
];

/** Kept from before the F22 redesign: shown as a second row under the design's six. */
const EXTRA_PRESETS: DatePreset[] = [
  { id: "yesterday", label: "Yesterday", period: { last: "Day", count: 1 } },
  { id: "thisMonth", label: "This month", period: { this: "Month" } },
  { id: "lastMonth", label: "Last month", period: { last: "Month", count: 1 } },
  { id: "thisYear", label: "This year", period: { this: "Year" } },
];

/** Calendar presets. */
const CALENDAR_PRESETS: DatePreset[] = [
  { id: "thisQuarter", label: "This quarter", period: { this: "Quarter" } },
  { id: "lastQuarter", label: "Last quarter", period: { last: "Quarter", count: 1 } },
  { id: "yearToDate", label: "Year to date", period: { to_date: "Year" } },
];

const ALL_LIVE = [...LIVE_PRESETS, ...CALENDAR_PRESETS, ...EXTRA_PRESETS];

export const toDatePresetOptions = (): FilterPillOption[] => [
  ...LIVE_PRESETS.map(({ id, label }) => ({ id, label })),
  ...CALENDAR_PRESETS.map(({ id, label }) => ({ id, label })),
];

/** The four extra live presets, for the second row. */
export const toExtraDatePresetOptions = (): FilterPillOption[] =>
  EXTRA_PRESETS.map(({ id, label }) => ({ id, label }));

export const toDatePresetRange = (
  id: string,
  now: Date,
  weekStart: WeekdayApi = DEFAULT_WEEK_START,
): DatePresetRange | null => {
  const preset = ALL_LIVE.find((candidate) => candidate.id === id);
  return preset ? toPeriodRange(preset.period, now, weekStart) : null;
};

export const samePeriod = (a: RelativePeriodApi, b: RelativePeriodApi): boolean =>
  a.this === b.this && a.last === b.last && a.count === b.count && a.to_date === b.to_date;

/** The rolling period a preset saves as; null for an unknown id. */
export const toDatePresetPeriod = (id: string): RelativePeriodApi | null =>
  ALL_LIVE.find((candidate) => candidate.id === id)?.period ?? null;

/** The preset a saved rolling period is, for its chip label and to reopen it; null when no preset says it. */
export const toDatePresetOfPeriod = (period: RelativePeriodApi): { id: string; label: string } | null => {
  const preset = ALL_LIVE.find((candidate) => samePeriod(candidate.period, period));
  return preset ? { id: preset.id, label: preset.label } : null;
};

/**
 * A rolling period in words, for a filter chip and a tile's subtitle alike: its preset's label
 * ("Last 30 days"), else built from the period ("Last 3 months", "This week", "Quarter to date").
 */
export const toPeriodText = (period: RelativePeriodApi): string => {
  const preset = toDatePresetOfPeriod(period);
  if (preset) return preset.label;
  if (period.to_date) return `${period.to_date} to date`;
  if (period.this) return `This ${period.this.toLowerCase()}`;
  const count = period.count ?? 1;
  const unit = (period.last ?? "").toLowerCase();
  return count === 1 ? `Last ${unit}` : `Last ${count} ${unit}s`;
};

/** The preset whose range equals the chosen one, if any (lights its pill). */
export const toActiveDatePresetId = (
  from: string,
  to: string,
  now: Date,
  weekStart: WeekdayApi = DEFAULT_WEEK_START,
): string | null =>
  ALL_LIVE.find((preset) => {
    const range = toDatePresetRange(preset.id, now, weekStart);
    return range?.from === from && range.to === to;
  })?.id ?? null;

const parse = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? day(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

const SHORT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const LONG = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });

/** "Jul 1 – Sep 30, 2026"; null until both ends are valid dates. */
export const formatResolvedRange = (from: string, to: string): string | null => {
  const start = parse(from);
  const end = parse(to);
  if (!start || !end) return null;
  if (start.getTime() === end.getTime()) return LONG.format(start);
  return start.getFullYear() === end.getFullYear()
    ? `${SHORT.format(start)} – ${LONG.format(end)}`
    : `${LONG.format(start)} – ${LONG.format(end)}`;
};

export type RelativeUnit = "days" | "weeks" | "months" | "quarters" | "years";

/** The "Last N" row's units, in menu order. */
export const RELATIVE_UNITS: readonly RelativeUnit[] = ["days", "weeks", "months", "quarters", "years"];

const RELATIVE_UNIT_PERIOD: Record<RelativeUnit, PeriodUnit> = {
  days: "Day",
  weeks: "Week",
  months: "Month",
  quarters: "Quarter",
  years: "Year",
};

/** "Last N units" as the rolling period it saves as; null for an amount that isn't a whole number from 1. */
export const toRelativePeriod = (amount: number, unit: RelativeUnit): RelativePeriodApi | null =>
  Number.isInteger(amount) && amount >= 1 ? { last: RELATIVE_UNIT_PERIOD[unit], count: amount } : null;

/** The whole number a "Last N" amount field holds, or null when blank, zero, negative, fractional or past a sane size. */
export const parseRelativeAmount = (text: string): number | null => {
  if (!/^\d{1,4}$/.test(text.trim())) return null;
  const amount = Number(text);
  return amount >= 1 ? amount : null;
};

/** "Last N units" as its dates today, read as the spec reads the period (whole units before today's). */
export const toRelativeRange = (
  amount: number,
  unit: RelativeUnit,
  now: Date,
  weekStart: WeekdayApi = DEFAULT_WEEK_START,
): DatePresetRange | null => {
  const period = toRelativePeriod(amount, unit);
  return period ? toPeriodRange(period, now, weekStart) : null;
};
